import { Mail01 } from '@untitledui/icons';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { validateEmail } from '@/lib/auth/constants';
import { Button } from '@/lib/base/buttons/button';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import { Input } from '@/lib/base/forms/input/input';
import { sanitizeError } from '@/lib/utils/errors';

import { useConfigInfo } from '../../allauth_auth/index.js';
import FormErrors from '../../components/FormErrors.tsx';
import { useTestLoginEnabled } from '../../hooks/useTestLoginEnabled';
import { AuthProcess, login } from '../../lib/allauth.js';
import type { AllauthResponse, Provider } from '../../types/allauth';
import ProviderList from './ProviderList.tsx';
import { TestLoginModal } from './TestLoginModal';

// =============================================================================
// Inner component (consumes FormContext)
// =============================================================================

interface LoginFormContentProps {
  email: string;
  changeEmail: (value: string) => void;
  password: string;
  changePassword: (value: string) => void;
  response: { fetching: boolean; content: AllauthResponse | null };
  setResponse: (r: {
    fetching: boolean;
    content: AllauthResponse | null;
  }) => void;
  syncOnlyError: string | null;
  hasProviders: boolean;
  loginByCodeEnabled: boolean;
  testLoginEnabled: boolean;
  openTestLogin: () => void;
}

function LoginFormContent({
  email,
  changeEmail,
  password,
  changePassword,
  response,
  setResponse,
  syncOnlyError,
  hasProviders,
  loginByCodeEnabled,
  testLoginEnabled,
  openTestLogin,
}: LoginFormContentProps) {
  const { touchAll, isValid, errorSummary, scrollToField } = useFormContext();

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      touchAll();
      if (!isValid) {
        if (errorSummary.length > 0) {
          requestAnimationFrame(() => scrollToField(errorSummary[0].name));
        }
        return;
      }
      setResponse({ fetching: true, content: null });

      login({ email: email.trim(), password })
        .then(content => {
          setResponse({ fetching: false, content });
        })
        .catch(e => {
          console.error(e);
          toast.error(sanitizeError(e));
          setResponse({ fetching: false, content: null });
        });
    },
    [
      touchAll,
      isValid,
      errorSummary,
      scrollToField,
      setResponse,
      email,
      password,
    ]
  );

  return (
    <div className='flex flex-col gap-8'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
          Welcome back
        </h1>
        <p className='text-base text-gray-600 dark:text-gray-400'>
          Welcome back! Please enter your details.
        </p>
      </div>

      <div className='flex flex-col gap-4'>
        <form onSubmit={onSubmit} noValidate className='flex flex-col gap-6'>
          {/* Sync-only account error */}
          {syncOnlyError && (
            <div className='bg-error-50 border border-error-200 rounded-lg p-4'>
              <p className='text-sm text-error-700'>{syncOnlyError}</p>
            </div>
          )}

          {/* Global form errors (like invalid credentials) */}
          <FormErrors errors={response.content?.errors} />

          {/* Helpful actions for login errors */}
          {response.content?.errors && response.content.errors.length > 0 && (
            <div className='bg-neutral-50 border border-neutral-200 rounded-lg p-4'>
              <h4 className='text-sm font-semibold text-neutral-800 mb-3'>
                Trouble signing in?
              </h4>
              <Link
                to='/account/password/reset'
                className='flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-100 ease-linear'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z'
                  />
                </svg>
                Reset your password
              </Link>
            </div>
          )}

          <div className='flex flex-col gap-5'>
            <div>
              <FormField name='email' label='Email' isRequired>
                {({ inputId, labelId }) => (
                  <Input
                    id={inputId}
                    aria-labelledby={labelId}
                    type='email'
                    className='w-full'
                    placeholder='Enter your email'
                    isRequired
                    onChange={changeEmail}
                    value={email}
                    name='email'
                    autoFocus
                  />
                )}
              </FormField>
              <FormErrors param='email' errors={response.content?.errors} />
            </div>

            <div>
              <FormField name='password' isRequired>
                {({ inputId }) => (
                  <>
                    <div className='flex justify-between items-center mb-1.5'>
                      {/* Hand-rolled rather than FormField's `label` prop so the
                          "Forgot password?" link can share the row. FormField
                          therefore renders no label and supplies no labelId, so
                          the id is derived from the unique inputId and passed to
                          the Input below — without it the Input's
                          placeholder-as-aria-label fallback masks this label and
                          the field announces "Enter your password". */}
                      <label
                        id={`${inputId}-label`}
                        className='block text-sm font-medium text-gray-700 dark:text-gray-200'
                        htmlFor={inputId}
                      >
                        Password
                      </label>
                      <Link
                        className='text-sm text-fg-brand-primary hover:text-utility-brand-700 font-medium'
                        to='/account/password/reset'
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <Input
                      id={inputId}
                      aria-labelledby={`${inputId}-label`}
                      type='password'
                      className='w-full'
                      placeholder='Enter your password'
                      isRequired
                      onChange={changePassword}
                      value={password}
                      name='password'
                    />
                  </>
                )}
              </FormField>
              <FormErrors param='password' errors={response.content?.errors} />
            </div>
          </div>

          <Button
            type='submit'
            isDisabled={response.fetching}
            className='w-full'
            isLoading={response.fetching}
            showTextWhileLoading
          >
            {response.fetching ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        {/* Social Login — outside the form so click does not trigger blur-based validation */}
        {hasProviders && (
          <ProviderList
            callbackURL='/account/provider/callback'
            process={AuthProcess.LOGIN}
            actionLabel='Sign in with'
          />
        )}
      </div>

      {/* Alternative Login Options */}
      {loginByCodeEnabled && (
        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-gray-200 dark:border-gray-700' />
          </div>
          <div className='relative flex justify-center'>
            <Button color='secondary'>
              <Link to='/account/login/code'>
                <div className='flex items-center gap-2'>
                  <Mail01 size={16} />
                  Email me a sign-in code
                </div>
              </Link>
            </Button>
          </div>
        </div>
      )}

      {testLoginEnabled && (
        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-gray-200 dark:border-gray-700' />
          </div>
          <div className='relative flex justify-center'>
            <Button color='secondary' onClick={openTestLogin}>
              Log in with test integration
            </Button>
          </div>
        </div>
      )}

      {/* Sign Up Link */}
      <div className='flex justify-center gap-1 text-center'>
        <span className='text-sm text-gray-600 dark:text-gray-400'>
          Don't have an account?
        </span>
        <Link
          to='/account/signup'
          className='text-sm font-semibold text-fg-brand-primary hover:text-utility-brand-700 transition-colors'
        >
          Sign up
        </Link>
      </div>
    </div>
  );
}

// =============================================================================
// Wrapper component (owns state, provides FormContext)
// =============================================================================

export const LoginContent = () => {
  const [email, changeEmail] = useState('');
  const [password, changePassword] = useState('');
  const [response, setResponse] = useState<{
    fetching: boolean;
    content: AllauthResponse | null;
  }>({ fetching: false, content: null });
  const [syncOnlyError, setSyncOnlyError] = useState<string | null>(null);
  const { providers, loginByCodeEnabled } = useConfigInfo();
  const [searchParams] = useSearchParams();
  const loginError = searchParams.get('error');
  const primaryEmail = searchParams.get('primary_email');

  useEffect(() => {
    if (loginError === 'sync_only_account' && primaryEmail) {
      setSyncOnlyError(
        `This account is for data sync only. Please sign in with ${decodeURIComponent(primaryEmail)}`
      );
    } else {
      setSyncOnlyError(null);
    }
  }, [loginError, primaryEmail]);

  const hasProviders =
    providers.filter((p: Provider) => p.client_id).length > 0;

  const { enabled: testLoginEnabled } = useTestLoginEnabled();
  const [testLoginOpen, setTestLoginOpen] = useState(false);
  const openTestLogin = useCallback(() => setTestLoginOpen(true), []);
  const closeTestLogin = useCallback(() => setTestLoginOpen(false), []);

  const fields = useMemo(
    () => ({
      email: {
        required: true,
        validate: (v: unknown) => validateEmail(v),
      },
      password: { required: true },
    }),
    []
  );

  const values = useMemo(() => ({ email, password }), [email, password]);

  return (
    <>
      <FormProvider fields={fields} values={values}>
        <LoginFormContent
          email={email}
          changeEmail={changeEmail}
          password={password}
          changePassword={changePassword}
          response={response}
          setResponse={setResponse}
          syncOnlyError={syncOnlyError}
          hasProviders={hasProviders}
          loginByCodeEnabled={loginByCodeEnabled}
          testLoginEnabled={testLoginEnabled}
          openTestLogin={openTestLogin}
        />
      </FormProvider>
      <TestLoginModal isOpen={testLoginOpen} onClose={closeTestLogin} />
    </>
  );
};
